// CF-ZH-L10N-V1 — pure i18n core (no React, no window). Unit-testable in plain Node.
// The React layer (index.tsx) is a thin wrapper over these functions.

export type Lang = 'en' | 'zh' | 'de' | 'pt';
export type Dict = Record<string, string>;
export type Vars = Record<string, string | number>;

/**
 * Map a Telegram `initDataUnsafe.user.language_code` to a supported UI language.
 * v1: any `zh*` code (zh, zh-Hans, zh-CN, zh-TW, zh-hant, ...) → Simplified Chinese.
 * Everything else (incl. undefined / malformed) → English. Never throws.
 */
export function pickLang(languageCode: unknown): Lang {
  if (typeof languageCode === 'string' && languageCode.trim().toLowerCase().startsWith('zh')) {
    return 'zh';
  }
  return 'en';
}

/**
 * Replace {var} placeholders with provided values. Unmatched placeholders are
 * left intact (so a missing var is visible, never silently blanked). Vars not
 * present in the template are ignored. Never throws.
 */
export function interpolate(template: string, vars?: Vars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    Object.prototype.hasOwnProperty.call(vars, name) ? String(vars[name]) : whole,
  );
}

/**
 * Resolve a key to display text for `lang`, with a strict fallback chain:
 *   lang dict → English dict → the key itself.
 * The key-as-last-resort guarantees the UI never renders empty for an unknown
 * key. English fallback guarantees a zh user never sees a blank where zh is
 * not yet translated. Then interpolate.
 */
export function resolve(
  lang: Lang,
  dicts: Record<Lang, Dict>,
  key: string,
  vars?: Vars,
): string {
  const template = dicts[lang][key] ?? dicts.en[key] ?? key;
  return interpolate(template, vars);
}
